import { useCallback, useEffect, useRef, type ReactNode, type RefObject } from "react";
import styled from "styled-components";
import { IconButton } from "../IconButton";
import { Spinner } from "../Spinner";
import type { DataTableSource, DataTableSourceSnapshot } from "./DataTableSource";
import type { TableRow } from "./TableRow";

export type DataTableAppendMessages = {
  retry?: ReactNode;
  loadingMore?: ReactNode;
  end?: ReactNode;
};

/** source가 요청 latch를 소유하므로 두 뷰와 keyboard가 같은 경계를 공유한다. */
export function useDataTableAppend<Row extends TableRow>(
  source: DataTableSource<Row>,
  snapshot: DataTableSourceSnapshot<Row>,
  scrollerRef: RefObject<HTMLDivElement | null>,
  scrollable: boolean,
) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const generation = snapshot.append?.generation;
  const page = snapshot.page;
  const requestMore = useCallback(() => {
    // observer callback은 disconnect 뒤에도 전달될 수 있어 최신 상태에서 판정한다.
    const current = source.getSnapshot();
    if (
      current.append?.generation !== generation ||
      current.page !== page ||
      current.append?.errorMessage != null
    )
      return;
    void source.loadMore();
  }, [source, generation, page]);

  useEffect(() => {
    if (generation !== undefined && scrollable && scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
  }, [source, generation, scrollable, scrollerRef]);

  const append = snapshot.append;
  useEffect(() => {
    if (
      !append?.hasMore ||
      append.loadingMore ||
      append.errorMessage !== null ||
      snapshot.loadState !== "loaded" ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    let active = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (active && entries.some((entry) => entry.isIntersecting)) requestMore();
      },
      { root: scrollable ? scrollerRef.current : null, rootMargin: "0px 0px 160px 0px" },
    );
    observer.observe(sentinel);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [append, snapshot.loadState, snapshot.page, requestMore, scrollerRef, scrollable]);

  return { sentinelRef, requestMore };
}

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: ${({ theme }) => theme.color.text.muted};
  font-size: ${({ theme }) => theme.typography.size.body};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
`;

const FooterRow = styled.div`
  /* 표 데이터의 min-width와 별개인 viewport 폭. 가로로 이동해도 sentinel/재시도는 남긴다. */
  position: sticky;
  left: 0;
`;

export function DataTableAppendFooter<Row extends TableRow>({
  source,
  snapshot,
  sentinelRef,
  messages = {},
  tableCellRole,
}: {
  source: DataTableSource<Row>;
  snapshot: DataTableSourceSnapshot<Row>;
  sentinelRef: RefObject<HTMLDivElement | null>;
  messages?: DataTableAppendMessages;
  tableCellRole?: "cell" | "gridcell";
}) {
  const append = snapshot.append;
  if (!append || snapshot.loadState === "idle" || snapshot.loadState === "loading") return null;
  const error = snapshot.loadState === "failed" ? snapshot.errorMessage : append.errorMessage;
  const showStatus = append.loadingMore || error !== null || !append.hasMore;
  const retry = () => void (snapshot.loadState === "failed" ? source.refresh() : source.loadMore());
  return (
    <FooterRow role={tableCellRole ? "row" : undefined} aria-hidden={!showStatus || undefined}>
      <div role={tableCellRole}>
        <div ref={sentinelRef} aria-hidden="true" />
        {showStatus ? (
          <Footer>
            {error !== null ? <span role="alert">{error}</span> : null}
            <span role="status" aria-live="polite">
              {append.loadingMore ? (
                <>
                  <Spinner size="sm" /> {messages.loadingMore ?? "Loading more…"}
                </>
              ) : !append.hasMore && error === null ? (
                (messages.end ?? "End of results.")
              ) : null}
            </span>
            {error !== null ? (
              <IconButton
                size="sm"
                variant="surface"
                onClick={retry}
                text={messages.retry ?? "Retry"}
              />
            ) : null}
          </Footer>
        ) : null}
      </div>
    </FooterRow>
  );
}
